<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { mdiMinus, mdiClose, mdiChevronUp, mdiArrowExpand, mdiPlay, mdiPause } from '@mdi/js'
import { assetTypeKey, getAssetTypeConfigFromString } from '../../config/assetTypeConfig'
import { useSharedAssetPlayerStore, toSharedAssetPlayerAsset } from '../../stores/useSharedAssetPlayerStore'
import { useAssetModals } from '../../composables/useAssetModals'

const { t } = useI18n()
const sharedPlayer = useSharedAssetPlayerStore()
const { assets: assetModals } = useAssetModals()
const { mobile } = useDisplay()
const isCollapsed = ref(false)

const isOpen = computed(() => sharedPlayer.mode === 'mini')

// On a phone the player is a bar above the bottom bar (docs/plans/mobile-navigation.md, lot 3): it
// adds its height to `--app-bottom-inset` through this class, so whatever floats lifts with it.
watch(isOpen, (open) => document.documentElement.classList.toggle('app-mini-player-open', open), {
  immediate: true,
})
onBeforeUnmount(() => document.documentElement.classList.remove('app-mini-player-open'))

// The bar has no room for the player's own controls: it plays and pauses what has a timeline.
const PLAYABLE = new Set(['video', 'audio', 'collection'])
const playable = computed(() => !!sharedPlayer.asset && PLAYABLE.has(assetTypeKey(sharedPlayer.asset.type)))

// Host the shared AssetPlayer is teleported into while docked. It stays mounted
// (the card uses v-show, not v-if) so the handoff back from the modal is seamless.
const playerHostEl = ref<HTMLElement | null>(null)
onMounted(() => sharedPlayer.setMiniHost(playerHostEl.value))
onBeforeUnmount(() => sharedPlayer.clearHost(playerHostEl.value))

// Reset the collapsed state whenever the played asset changes.
watch(() => sharedPlayer.asset?.uid, () => {
  isCollapsed.value = false
})

function close() {
  sharedPlayer.close()
  isCollapsed.value = false
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
}

async function openFullscreen() {
  const asset = sharedPlayer.asset
  if (!asset) return
  await assetModals.preview({
    asset: toSharedAssetPlayerAsset(asset),
    showOpenDetails: !asset.viaAssetId,
    viaAssetId: asset.viaAssetId,
    startAt: sharedPlayer.position,
  })
}
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-show="isOpen"
        class="floating-mini-player"
        :class="{ 'floating-mini-player--bar': mobile }"
      >
        <div
          class="floating-mini-player__card"
          :class="{ 'floating-mini-player__card--collapsed': isCollapsed && !mobile }"
        >
          <!-- Header -->
          <div v-if="sharedPlayer.asset" class="floating-mini-player__header">
            <!-- On a phone the name is the way to the full player, as the thumbnail is. -->
            <button
              v-if="mobile"
              type="button"
              class="floating-mini-player__open"
              :aria-label="t('assets.miniPlayer.openLarge', { name: sharedPlayer.asset.name })"
              @click="openFullscreen"
            >
              <span class="floating-mini-player__name">{{ sharedPlayer.asset.name }}</span>
            </button>
            <template v-else>
              <v-icon
                :icon="getAssetTypeConfigFromString(sharedPlayer.asset.type).icon"
                :color="getAssetTypeConfigFromString(sharedPlayer.asset.type).color"
                size="16"
              />
              <span class="floating-mini-player__name">{{ sharedPlayer.asset.name }}</span>
            </template>
            <div class="floating-mini-player__actions">
              <template v-if="mobile">
                <v-btn
                  v-if="playable"
                  variant="text"
                  :size="44"
                  :icon="sharedPlayer.playing ? mdiPause : mdiPlay"
                  :aria-label="sharedPlayer.playing ? t('assets.miniPlayer.pause') : t('assets.miniPlayer.play')"
                  @click="sharedPlayer.togglePlayback()"
                />
                <v-btn
                  variant="text"
                  :size="44"
                  :icon="mdiClose"
                  :aria-label="t('assets.miniPlayer.close')"
                  @click="close"
                />
              </template>
              <template v-else>
                <v-btn
                  variant="text"
                  density="compact"
                  size="small"
                  :icon="isCollapsed ? mdiChevronUp : mdiMinus"
                  @click="toggleCollapse"
                >
                  <v-icon :icon="isCollapsed ? mdiChevronUp : mdiMinus" size="18" />
                  <v-tooltip activator="parent" location="top">
                    {{ isCollapsed ? t('assets.miniPlayer.expand') : t('assets.miniPlayer.collapse') }}
                  </v-tooltip>
                </v-btn>
                <v-btn
                  variant="text"
                  density="compact"
                  size="small"
                  :icon="mdiArrowExpand"
                  @click="openFullscreen"
                >
                  <v-icon :icon="mdiArrowExpand" size="18" />
                  <v-tooltip activator="parent" location="top">
                    {{ t('assets.miniPlayer.fullscreen') }}
                  </v-tooltip>
                </v-btn>
                <v-btn
                  variant="text"
                  density="compact"
                  size="small"
                  :icon="mdiClose"
                  @click="close"
                >
                  <v-icon :icon="mdiClose" size="18" />
                  <v-tooltip activator="parent" location="top">
                    {{ t('assets.miniPlayer.close') }}
                  </v-tooltip>
                </v-btn>
              </template>
            </div>
          </div>

          <!-- Player content: the shared AssetPlayer is teleported here. On a phone it is the
               bar's thumbnail, and a tap on it opens the full player. -->
          <div class="floating-mini-player__content" @click="mobile && openFullscreen()">
            <div ref="playerHostEl" class="floating-mini-player__host" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
@use '@/styles/design-tokens' as *;

.floating-mini-player {
  position: fixed;
  bottom: $space-xl;
  right: $space-xl;
  z-index: 1900;

  &__card {
    width: 560px;
    max-width: calc(100vw - #{$space-xl} * 2);
    border-radius: $radius-lg;
    box-shadow: $shadow-lg;
    background: rgb(var(--v-theme-surface));
    overflow: hidden;
    @include transition-normal;

    &--collapsed {
      .floating-mini-player__content {
        height: 0;
      }
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-sm $space-sm $space-sm $space-md;
    border-bottom: 1px solid rgba(var(--v-border-color), $opacity-border);
    min-height: 40px;
  }

  &__name {
    flex: 1;
    font-size: $text-sm;
    font-weight: $font-semibold;
    line-height: $leading-tight;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }

  &__content {
    aspect-ratio: 16 / 9;
    background: #000;
  }

  &__host {
    width: 100%;
    height: 100%;
  }
}

// A phone: a full-width bar of `$miniplayer-h` on top of the bottom bar, the player as its
// thumbnail on the left. Its height is what `--app-bottom-inset` adds while it is open.
.floating-mini-player--bar {
  left: 0;
  right: 0;
  bottom: calc(var(--app-tabbar-h) + env(safe-area-inset-bottom, 0px));
  z-index: 1006;

  // Over a temporary drawer, stacked with the layout under 1006, the bar would cover its last
  // 46 px, where its buttons are: it gives way while one is open, the sound going on.
  html:has(.v-navigation-drawer--temporary.v-navigation-drawer--active) & {
    visibility: hidden;
  }

  .floating-mini-player__card {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: none;
    height: $miniplayer-h;
    border-radius: 0;
    box-shadow: none;
    border-top: 1px solid rgba(var(--v-border-color), $opacity-border);
  }

  .floating-mini-player__header {
    order: 1;
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 0 $space-xs 0 0;
    gap: 0;
    border-bottom: none;
  }

  .floating-mini-player__content {
    height: 100%;
    flex-shrink: 0;
    cursor: pointer;
  }

  // The player is a picture here: its controls are the bar's.
  .floating-mini-player__host {
    pointer-events: none;

    // Its overlays too — the play and volume flashes, the control bar — which an 80 px
    // picture cannot hold; the bar's button says what changed.
    :deep(.video-content > :not(video)) {
      display: none;
    }
  }

  .floating-mini-player__actions {
    gap: 0;
  }
}

.floating-mini-player__open {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 $space-sm 0 $space-md;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;

  @include focus-visible-only;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all $duration-normal $ease-default;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
